import React, { useState } from "react";
import FaqSection from "../components/FaqSection";
import { Helmet } from "react-helmet-async";
import zohoStyles from "./ZohoFinal.module.css"; // ✅ Using Zoho CSS
import Navigation from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Plus,
  Minus,
  Phone,
  Mail,
  ArrowRight,
  Building2,
  Shield,
  PlugZap,
  ServerCog,
  Package,
  Workflow,
  Layers,
} from "lucide-react";

/* ✅ Import images */
import OdooLogo from "../assests/images/OdooTop.png";
// Add your client logos for case studies
// import ClientLogo1 from "../assests/images/client1.png";
// import ClientLogo2 from "../assests/images/client2.png";
// import ClientLogo3 from "../assests/images/client3.png";

const siteUrl = "https://www.cloudare.in/services-odoo";
const phoneDisplay = "+1 628-282-5683";
const phoneHref = "+16282825683";
const email = "sales@cloudare.in";
const calendly = "https://calendly.com/cloudare";

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Cloudare Technologies",
  url: "https://www.cloudare.in",
  logo: "https://www.cloudare.in/logo192.png",
  sameAs: [],
};

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Odoo ERP Implementation & Support",
  description:
    "Cloudare provides comprehensive Odoo ERP & CRM solutions—including implementations, customization, integrations, accounting & GST configuration, inventory & MRP management, and managed support—for businesses across India, KSA, Malaysia, the US, Canada, and other global markets.",
  provider: {
    "@type": "Organization",
    name: "Cloudare Technologies",
    url: "https://www.cloudare.in",
  },
  areaServed: [
    "Raipur (Chhattisgarh, India)",
    "Indore (Madhya Pradesh, India)",
    "Bhubaneswar (Odisha, India)",
    "Nagpur (Maharashtra, India)",
    "Jaipur (Rajasthan, India)",
    "Central India",
    "Kingdom of Saudi Arabia (KSA)",
    "Malaysia",
    "United States",
    "Canada",
  ],
};

const faqList = [
  {
    q: "Do you provide Odoo implementation for manufacturing (MRP)?",
    a: "Yes — we implement Odoo Manufacturing with BOMs, routings, work orders, quality checks, and barcode integration to streamline your production processes.",
  },
  {
    q: "Can you migrate our data from legacy ERPs to Odoo?",
    a: "Absolutely — we handle data extraction, cleansing, mapping, and validated imports to ensure smooth migration and accurate reconciliation.",
  },
  {
    q: "Do you integrate Odoo with third-party systems like payments and logistics?",
    a: "Yes — we enable seamless REST/SOAP/API integrations, middleware solutions, and custom connectors for payment gateways, shipping, marketplaces, and tax systems.",
  },
  {
    q: "Do you provide Odoo support and managed services?",
    a: "Yes — we offer SLA-based managed services, ticket packs, release & upgrade support, and comprehensive training to keep your Odoo environment running smoothly.",
  },
  {
    q: "Can you customize Odoo to fit our business requirements?",
    a: "Absolutely — our experienced developers build custom modules, reports, dashboards, and workflow automations tailored to your unique business processes.",
  },
  {
    q: "Do you assist with Odoo accounting & GST/VAT compliance?",
    a: "Yes — we configure Indian GST and local tax packs, perform reconciliations, and generate statutory reports to ensure full compliance.",
  },
  {
    q: "What deployment options are available for Odoo?",
    a: "We support SaaS-hosted, private cloud, on-premise, and hybrid deployments, offering flexibility based on your business needs.",
  },
  {
    q: "How long does an average Odoo implementation take?",
    a: "Implementation timelines vary by scope — SMEs typically take 6–12 weeks, and we provide a detailed timeline after our discovery session.",
  },
  {
    q: "Do you offer training for Odoo users and admins?",
    a: "Yes — we provide role-based training for admins, executives, and end-users, complete with documentation and recorded sessions for easy reference.",
  },
  {
    q: "Can you implement multi-warehouse & multi-company structures in Odoo?",
    a: "Absolutely — multi-company, multi-warehouse, and multi-currency setups are part of our core Odoo services to support growing businesses.",
  },
  {
    q: "Do you provide Odoo services for businesses in Raipur (CG)?",
    a: "Yes — we support SMEs and enterprises in Raipur (Chhattisgarh) with full Odoo implementation, accounting & GST compliance, and managed services tailored to local business needs.",
  },
  {
    q: "Do you provide Odoo services for businesses in Tier-2 cities?",
    a: "Yes — we support SMEs and enterprises in Tier-2 cities including Raipur (Chhattisgarh/CG), Indore (Madhya Pradesh), Bhubaneswar (Odisha), Nagpur (Maharashtra), Jaipur (Rajasthan), and more. Our services cover end-to-end Odoo implementations, seamless integrations, accounting & GST configuration, inventory & MRP management, and fully managed support tailored to your business needs.",
  },
  {
    q: "Do you provide Odoo services for businesses outside India?",
    a: "Yes — we serve businesses across multiple countries including KSA, Malaysia, the US, Canada, and other global markets. Our offerings include complete Odoo implementations, integrations, compliance configurations (where applicable), and managed support customized for each region.",
  },
];

export default function OdooPage() {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <main className={zohoStyles.main}>
      <Navigation />
      <Helmet>
        <title>
          Odoo Partner in Raipur (CG) — Odoo ERP Implementation & Support |
          Cloudare
        </title>
        <meta
          name="description"
          content="Cloudare — Odoo Partner in Raipur, Chhattisgarh (CG). Odoo ERP & CRM implementation, customization, integrations, accounting, inventory/MRP, managed support across India & globally."
        />
        <meta
          name="keywords"
          content="Odoo Raipur, Odoo CG, Odoo Chhattisgarh, Odoo Odisha, Odoo ERP, Odoo CRM Raipur, Odoo Implementation, Odoo Support, Odoo MRP, Odoo Integrations"
        />
        <link rel="canonical" href={siteUrl} />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqList.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className={zohoStyles.hero}>
        <div className={zohoStyles.containerGrid}>
          <div className={zohoStyles.headContainer}>
            <img
              src={OdooLogo}
              alt="Odoo ERP logo"
              className={zohoStyles.heroLogo}
            />
            <h1 className={zohoStyles.heroTitle1}>
              Odoo ERP & CRM — Implementation, Customization & Support
            </h1>
            <p className={zohoStyles.heroDesc}>
              We are a trusted Odoo Partner serving clients across India and
              worldwide — providing comprehensive Odoo solutions, including
              end-to-end implementations, seamless integrations, accounting &
              GST configuration, inventory & MRP management, and fully managed
              support customized to your business requirements.
            </p>

            <div className={zohoStyles.heroButtons}>
              <a
                href={calendly}
                target="_blank"
                rel="noopener noreferrer"
                className={zohoStyles.btnPrimary}
              >
                Book a Free Discovery{" "}
                <ArrowRight className={zohoStyles.iconSm} />
              </a>
              <a href="#faq" className={zohoStyles.btnSecondary}>
                View FAQs <ArrowRight className={zohoStyles.iconSm} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>Odoo Services We Offer</h2>
          <div className={zohoStyles.appGrid}>
            <AppCard
              title="Implementation & Rollout"
              desc="Discovery, process mapping, configuration, data migration, UAT and go-live."
              icon={<Building2 className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Customization & Development"
              desc="Custom modules, reports, UI tweaks, business logic and workflows."
              icon={<ServerCog className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Integrations"
              desc="Payment gateways, shipping, e-invoicing, marketplaces, and ERP integrations."
              icon={<PlugZap className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Accounting & GST / Local Tax"
              desc="Odoo Accounting setup, reconciliations, GST/VAT configuration and reporting."
              icon={<Shield className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Inventory, WMS & MRP"
              desc="Multi-warehouse, serial/batch tracking, barcodes, MRP and quality checks."
              icon={<Package className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Managed Services & Support"
              desc="SLA-based support, upgrades, backups, monitoring and training."
              icon={<Workflow className={zohoStyles.iconLg} />}
            />
          </div>
        </div>
      </section>

      {/* Why Choose Odoo */}
      <section className={zohoStyles.sectionAlt}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>
            Why Choose Odoo with Cloudare
          </h2>
          <div className={zohoStyles.featureGrid}>
            <div className={zohoStyles.featureCard}>
              <div className={zohoStyles.featureHeader}>
                <div className={zohoStyles.featureIcon}>
                  <Building2 className={zohoStyles.iconLg} />
                </div>
                <h3 className={zohoStyles.featureTitle}>
                  Modular & Affordable
                </h3>
              </div>
              <p className={zohoStyles.featureDesc}>
                Pick only the apps you need — CRM, Sales, Purchase, Inventory,
                Manufacturing, Accounting, HR and more.
              </p>
            </div>

            <div className={zohoStyles.featureCard}>
              <div className={zohoStyles.featureHeader}>
                <div className={zohoStyles.featureIcon}>
                  <ServerCog className={zohoStyles.iconLg} />
                </div>
                <h3 className={zohoStyles.featureTitle}>Fully Customizable</h3>
              </div>
              <p className={zohoStyles.featureDesc}>
                Extend Odoo with custom modules, reports, dashboards and
                workflows tailored to your business.
              </p>
            </div>

            <div className={zohoStyles.featureCard}>
              <div className={zohoStyles.featureHeader}>
                <div className={zohoStyles.featureIcon}>
                  <Layers className={zohoStyles.iconLg} />
                </div>
                <h3 className={zohoStyles.featureTitle}>
                  Integrated End-to-End
                </h3>
              </div>
              <p className={zohoStyles.featureDesc}>
                From sales to accounting and manufacturing — single source of
                truth with better reporting.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      {/* <section className={zohoStyles.sectionAlt}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>Success Stories</h2>
          <p className={zohoStyles.sectionDesc}>
            Selected Odoo deployments and outcomes we delivered.
          </p>
          <div className={zohoStyles.caseGrid}>
            <CaseStudy
              logo={OdooLogo} // Replace with actual client logo
              alt="Manufacturing Client logo"
              title="Manufacturing — Odoo MRP & Inventory"
              bullets={[
                "Complete MRP with BOMs & work orders",
                "Barcode integration & quality checks",
              ]}
            />
            <CaseStudy
              logo={OdooLogo} // Replace with actual client logo
              alt="Retail Client logo"
              title="Retail — Odoo POS & Accounting"
              bullets={[
                "Multi-location POS setup",
                "Automated GST reconciliations",
              ]}
            />
            <CaseStudy
              logo={OdooLogo} // Replace with actual client logo
              alt="Distribution Client logo"
              title="Distribution — Odoo WMS & Sales"
              bullets={[
                "Multi-warehouse management",
                "Integrated sales & delivery tracking",
              ]}
            />
          </div>
          <div className={zohoStyles.statGrid}>
            <StatCard
              label="Successful Implementations"
              value="30+"
              maxProgress={55}
            />
            <StatCard
              label="Client Satisfaction"
              value="98%"
              maxProgress={85}
            />
            <StatCard
              label="Average Go-Live"
              value="6-12 wks"
              maxProgress={50}
            />
          </div>
        </div>
      </section> */}

      {/* Testimonials */}
      {/* <section className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>What Our Clients Say</h2>
          <div className={zohoStyles.grid3}>
            <Testimonial
              name="Operations Head, Manufacturing Co."
              text="Cloudare's Odoo MRP implementation transformed our production workflow. Excellent execution."
            />
            <Testimonial
              name="Finance Manager, Retail Chain"
              text="GST compliance and accounting setup was seamless. Great support throughout."
            />
            <Testimonial
              name="CEO, Distribution Firm"
              text="Multi-warehouse setup improved our inventory accuracy by 95%. Highly recommend."
            />
          </div>
        </div>
      </section> */}

      {/* Contact CTA */}
      <section id="contact" className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <div className={zohoStyles.contactCard}>
            <div className={zohoStyles.contact1}>
              <h2 className={zohoStyles.sectionTitle}>
                Talk to an Odoo Expert
              </h2>
              <p className={zohoStyles.heroDesc}>Book a free 30-min Call.</p>
              <div className={zohoStyles.heroButtons}>
                {/* <a href={calendly} className={zohoStyles.btnPrimary}>
                  Book on Calendly <ArrowRight className={zohoStyles.iconSm} />
                </a> */}
                <a
                  href={calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={zohoStyles.btnPrimary}
                >
                  Book on Calendly
                  <ArrowRight className={zohoStyles.iconSm} />
                </a>
                <a href={`mailto:${email}`} className={zohoStyles.btnSecondary}>
                  <Mail className={zohoStyles.iconSm} /> {email}
                </a>
              </div>
            </div>
            <div className={zohoStyles.contactInfo}>
              <ContactRow
                label="Phone"
                value={phoneDisplay}
                href={`tel:${phoneHref}`}
                icon={<Phone className={zohoStyles.iconSm} />}
              />
            </div>
          </div>
        </div>
      </section>

      <div id="faq">
        <FaqSection
          title="Odoo ERP — FAQ"
          faqs={faqList.map((x) => ({ question: x.q, answer: x.a }))}
          emitJsonLd
        />
      </div>

      <Footer />
    </main>
  );
}

/* ✅ Subcomponents (same pattern as Zoho & NetSuite pages) */

function AppCard({ title, desc, icon }) {
  return (
    <div className={zohoStyles.appCard}>
      <div className={zohoStyles.appHeader}>
        <div className={zohoStyles.appIcon}>{icon}</div>
        <h3 className={zohoStyles.appTitle}>{title}</h3>
      </div>
      <p className={zohoStyles.appDesc}>{desc}</p>
    </div>
  );
}

// function CaseStudy({ logo, alt, title, bullets }) {
//   return (
//     <div className={zohoStyles.caseCard}>
//       <div className={zohoStyles.caseImage}>
//         <img src={logo} alt={alt} className={zohoStyles.caseLogo} />
//       </div>
//       <div>
//         <h3 className={zohoStyles.caseTitle}>{title}</h3>
//         <ul className={zohoStyles.caseList}>
//           {bullets.map((b, i) => (
//             <li key={i}>{b}</li>
//           ))}
//         </ul>
//       </div>
//     </div>
//   );
// }

// function StatCard({ label, value, maxProgress = 70 }) {
//   const [progress, setProgress] = useState(0);

//   useEffect(() => {
//     let start = 0;
//     const duration = 1200;
//     const stepTime = 20;
//     const step = Math.ceil(maxProgress / (duration / stepTime));

//     const interval = setInterval(() => {
//       start += step;
//       if (start >= maxProgress) {
//         start = maxProgress;
//         clearInterval(interval);
//       }
//       setProgress(start);
//     }, stepTime);

//     return () => clearInterval(interval);
//   }, [maxProgress]);

//   const radius = 45;
//   const circumference = 2 * Math.PI * radius;
//   const offset = circumference - (progress / 100) * circumference;

//   return (
//     <div className={zohoStyles.statCard}>
//       <svg
//         className={zohoStyles.circleChart}
//         width="100"
//         height="100"
//         viewBox="0 0 100 100"
//       >
//         <circle
//           className={zohoStyles.circleBg}
//           cx="50"
//           cy="50"
//           r={radius}
//           strokeWidth="10"
//           fill="none"
//         />
//         <circle
//           className={zohoStyles.circleProgress}
//           cx="50"
//           cy="50"
//           r={radius}
//           strokeWidth="10"
//           fill="none"
//           strokeDasharray={circumference}
//           strokeDashoffset={offset}
//         />
//         <text
//           x="50"
//           y="50"
//           textAnchor="middle"
//           dominantBaseline="middle"
//           className="statValue"
//         >
//           {value}
//         </text>
//       </svg>
//       <div className="statLabel">{label}</div>
//     </div>
//   );
// }

// function Testimonial({ name, text }) {
//   return (
//     <div className={zohoStyles.proCard}>
//       <div className={zohoStyles.testimonialBox}>
//         <div className={zohoStyles.avatar}></div>
//         <div>
//           <div className={zohoStyles.testimonialName}>{name}</div>
//           <div className={zohoStyles.testimonialText}>{text}</div>
//         </div>
//       </div>
//     </div>
//   );
// }

function ContactRow({ label, value, href, icon }) {
  return (
    <div className={zohoStyles.contactRow}>
      <div className={zohoStyles.contactIcon}>{icon}</div>
      <div>
        <div className={zohoStyles.contactLabel}>{label}</div>
        <a href={href} className={zohoStyles.contactValue}>
          {value}
        </a>
      </div>
    </div>
  );
}
